import React, { Component } from 'react';
import { selectablePaymentMethods } from '../utils/enums';

var config = process.env;

class GeneralSettingsComponent extends Component {

    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            isSubmitting: false,
            branches: [],
            defaultBranchId: '',
            oosProviderDays: '',
            competitorPriceThresholdPct: '',
            // ENI-279: en PORCENTAJE (3,5), que es como el dueño piensa la tasa. El backend
            // las guarda como fracción (0.035), que es lo que consume la cascada de costos.
            cardFeePos: '',
            // Sync con proveedor: si cada corrida además MARCA los faltantes o sólo observa.
            saccumSyncApplyRules: false,
            // Si el feature maestro (`saccum_sync`) está prendido. `null` = no se pudo averiguar:
            // en ese caso no se afirma nada, porque decir "está apagado" sin saberlo manda a
            // buscar un problema que no existe.
            saccumSyncEnabled: null,
            // { [paymentType]: code } — qué cupón se aplica a cada medio de pago.
            paymentDiscountCodes: {},
            // Cupones de tipo "por medio de pago" disponibles para elegir.
            paymentDiscountOptions: [],
            // Gift cards (FR-083): meses de validez de las que se vendan. '' = no vencen.
            giftCardValidityMonths: '',
        };
    }

    setPaymentDiscountCode = (method, code) => {
        const codes = { ...this.state.paymentDiscountCodes };
        if (code === '') {
            delete codes[method];
        } else {
            codes[method] = code;
        }
        this.setState({ paymentDiscountCodes: codes });
    }

    componentDidMount() {
        this.loadData();
    }

    // Fuera del Promise.all de loadData a propósito: si este GET falla, la pantalla tiene que
    // cargar igual. Metido en el Promise.all, una caída de este endpoint dejaba toda la
    // configuración general en blanco por un dato que sólo decora una sección.
    loadSaccumSyncFlag = async (request) => {
        try {
            const response = await fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}configuration/settings/feature-flags`, request);
            const body = await response.json();
            if (body.error !== 0 || !body.data || !body.data.flags) {
                return null;
            }
            const flag = body.data.flags.find((item) => item.key === 'saccum_sync');
            return flag ? !!flag.value : null;
        } catch (error) {
            return null;
        }
    }

    loadData = async () => {
        this.setState({ loading: true });

        const request = {
            method: 'GET',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            },
        };

        try {
            const [settingsResponse, branchesResponse, discountCodesResponse] = await Promise.all([
                fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}configuration/settings/general`, request),
                fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}branch`, request),
                fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}discountCode/paymentMethod`, request),
            ]);

            const settingsData = await settingsResponse.json();
            const branchesData = await branchesResponse.json();
            const discountCodesData = await discountCodesResponse.json();
            const saccumSyncEnabled = await this.loadSaccumSyncFlag(request);

            if (settingsData.error > 0) {
                alert(settingsData.message);
                return;
            }

            if (branchesData.error > 0) {
                alert(branchesData.message);
                return;
            }

            this.setState({
                branches: branchesData.data || [],
                defaultBranchId: String(settingsData.data?.defaultBranchId || ''),
                oosProviderDays: String(settingsData.data?.oosProviderDays || ''),
                competitorPriceThresholdPct: String(settingsData.data?.competitorPriceThresholdPct ?? ''),
                // `?? 0` y no `|| ''`: 0 es un valor cargado legítimo ("no se descuenta") y con
                // `||` se mostraría el campo vacío como si nunca se hubiera configurado.
                cardFeePos: String(settingsData.data?.cardFeePos ?? 0),
                // `??` y no `||`: `false` es el valor guardado más importante de esta pantalla
                // (es el shadow mode) y con `||` se leería como "no configurado".
                saccumSyncApplyRules: settingsData.data?.saccumSyncApplyRules ?? false,
                saccumSyncEnabled,
                paymentDiscountCodes: settingsData.data?.paymentDiscountCodes || {},
                paymentDiscountOptions: discountCodesData.data || [],
                // `?? ''`: null es "no vencen" y se muestra vacío, no como "null".
                giftCardValidityMonths: String(settingsData.data?.giftCardValidityMonths ?? ''),
                loading: false,
            });
        } catch (error) {
            this.setState({ loading: false });
        }
    }

    onSubmit = async () => {
        this.setState({ isSubmitting: true });

        try {
            const response = await fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}configuration/settings/general`, {
                method: 'PUT',
                headers: {
                    'access-token': localStorage.getItem('token'),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    defaultBranchId: Number(this.state.defaultBranchId || 1),
                    oosProviderDays: Number(this.state.oosProviderDays || 30),
                    saccumSyncApplyRules: this.state.saccumSyncApplyRules,
                    paymentDiscountCodes: this.state.paymentDiscountCodes,
                    // Se manda el string tal cual: el backend rechaza el vacío en vez de
                    // guardarlo como 0, que es lo que haría un Number('') acá.
                    cardFeePos: this.state.cardFeePos,
                    // Precios vs competencia. Van como string y sólo si están cargados: el backend
                    // valida el rango y rechaza con su mensaje; vacío = no tocar (un backend
                    // anterior a la funcionalidad no los manda y el campo queda vacío).
                    ...(this.state.competitorPriceThresholdPct !== '' ? { competitorPriceThresholdPct: this.state.competitorPriceThresholdPct } : {}),
                    // String tal cual: '' = no vencen. Un Number('') mandaría 0, que el backend
                    // rechaza (3310) en vez de entenderlo como "sin vencimiento".
                    giftCardValidityMonths: this.state.giftCardValidityMonths.trim(),
                }),
            });

            const data = await response.json();
            if (data.error > 0) {
                alert(data.message);
                return;
            }

            this.setState({
                defaultBranchId: String(data.data?.defaultBranchId || this.state.defaultBranchId),
                oosProviderDays: String(data.data?.oosProviderDays || this.state.oosProviderDays),
                cardFeePos: String(data.data?.cardFeePos ?? this.state.cardFeePos),
                competitorPriceThresholdPct: String(data.data?.competitorPriceThresholdPct ?? this.state.competitorPriceThresholdPct),
                saccumSyncApplyRules: data.data?.saccumSyncApplyRules ?? this.state.saccumSyncApplyRules,
                paymentDiscountCodes: data.data?.paymentDiscountCodes || {},
                giftCardValidityMonths: String(data.data?.giftCardValidityMonths ?? ''),
            });
        } finally {
            this.setState({ isSubmitting: false });
        }
    }

    render() {
        return (
            <div className="card">
                <div className="d-flex justify-content-between align-items-center border-bottom" style={{ padding: '1rem 1.5rem' }}>
                    <span className="text-muted" style={{ fontSize: '0.95rem' }}>
                        Definí parámetros globales del sistema que afectan el comportamiento general del backoffice.
                    </span>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={this.loadData}>
                        Recargar
                    </button>
                </div>
                <div className="card-body">
                    {this.state.loading ? (
                        <div className="containerLoader" style={{ display: 'block', position: 'static', width: '100%' }}>
                            <div className="loader"></div>
                        </div>
                    ) : (
                        <div className="row">
                            <div className="col-lg-6">
                                <div className="form-group">
                                    <label>Sucursal principal</label>
                                    <select
                                        className="custom-select"
                                        value={this.state.defaultBranchId}
                                        onChange={(event) => this.setState({ defaultBranchId: event.target.value })}
                                    >
                                        <option value="">Seleccionar</option>
                                        {this.state.branches.map((branch) => (
                                            <option key={branch.id} value={branch.id}>{branch.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="form-group">
                                    <label>Días para sugerencia OOS</label>
                                    <input
                                        type="number"
                                        min="0"
                                        className="form-control"
                                        value={this.state.oosProviderDays}
                                        onChange={(event) => this.setState({ oosProviderDays: event.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="col-12">
                                <hr />
                                <h6 className="mb-1">Comisiones de cobro</h6>
                                <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                                    Qué porcentaje se lleva el medio de pago sobre lo cobrado. Se descuenta en el
                                    detalle de cada venta y en los paneles de estadísticas. Mientras estén en 0 la
                                    ganancia que muestran esas pantallas es un <strong>techo</strong>, no un
                                    resultado. Las ventas de MercadoLibre no usan estas tasas: la comisión de ML ya
                                    incluye el procesamiento del pago.
                                </p>
                            </div>
                            <div className="col-lg-6">
                                <div className="form-group">
                                    <label>Comisión POS (%)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        className="form-control"
                                        value={this.state.cardFeePos}
                                        onChange={(event) => this.setState({ cardFeePos: event.target.value })}
                                    />
                                    <small className="form-text text-muted">
                                        Ejemplo: 3,5 para 3,5&nbsp;%. La comisión de Mercado Pago
                                        no se carga acá: se toma de cada cobro real.
                                    </small>
                                </div>
                            </div>
                            <div className="col-12">
                                <hr />
                                <h6 className="mb-1">Precios vs competencia</h6>
                                <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                                    Cuándo un producto cuenta como <strong>caro</strong> frente a las otras tiendas.
                                    Sólo aplica con el interruptor
                                    &laquo;Precios vs competencia&raquo; encendido.
                                </p>
                            </div>
                            <div className="col-lg-6">
                                <div className="form-group">
                                    <label>Umbral de diferencia (%)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="100"
                                        step="1"
                                        className="form-control"
                                        value={this.state.competitorPriceThresholdPct}
                                        onChange={(event) => this.setState({ competitorPriceThresholdPct: event.target.value })}
                                    />
                                    <small className="form-text text-muted">
                                        Con 10, un producto es caro cuando está más de 10&nbsp;% arriba de la
                                        mediana de las tiendas con stock. Entero entre 1 y 100.
                                    </small>
                                </div>
                            </div>
                            <div className="col-12">
                                <hr />
                                <h6 className="mb-1">Sync con proveedor (SACCUM)</h6>
                                <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                                    El sync lee todos los días qué publica SACCUM con stock. Esta opción decide
                                    si además <strong>marca los faltantes</strong> o sólo observa. Apagada, el
                                    Monitor de Stock depende del marcado a mano y nada avisa cuando el proveedor
                                    repone. Prendida, cada corrida marca sola lo que el proveedor no tiene y
                                    levanta la marca cuando vuelve a tenerlo. Nunca pisa un faltante marcado a
                                    mano: si lo contradice, lo manda a "REVISAR: el proveedor repuso" para que
                                    lo resuelva una persona.
                                </p>
                            </div>
                            <div className="col-12 mb-3">
                                <div className="custom-control custom-toggle">
                                    <input
                                        type="checkbox"
                                        id="saccum-sync-apply-rules"
                                        className="custom-control-input"
                                        checked={this.state.saccumSyncApplyRules}
                                        onChange={(event) => this.setState({ saccumSyncApplyRules: event.target.checked })}
                                    />
                                    <label
                                        className="custom-control-label"
                                        htmlFor="saccum-sync-apply-rules"
                                        style={{ lineHeight: '1.75rem', cursor: 'pointer' }}
                                    >
                                        Marcar faltantes automáticamente
                                    </label>
                                </div>
                                {this.state.saccumSyncEnabled === false ? (
                                    <div className="alert alert-warning mt-2 mb-0" style={{ fontSize: '0.85rem' }}>
                                        El sync con el proveedor está apagado en Configuración &rarr; Feature flags
                                        (&ldquo;Sync con proveedor (SACCUM)&rdquo;). Mientras siga apagado esta
                                        opción queda guardada pero no hace nada.
                                    </div>
                                ) : null}
                            </div>
                            <div className="col-12">
                                <hr />
                                <h6 className="mb-1">Descuento por medio de pago</h6>
                                <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                                    Elegí qué código de descuento se aplica solo según cómo pague el cliente, en la
                                    venta de mostrador (Ventas &rarr; Nueva venta). No afecta a la tienda online, ni a
                                    cafetería, mesas o eventos, ni a los cambios. Si el cliente además trae un cupón se
                                    aplica el mayor de los dos, nunca los dos juntos. La venta queda registrada con el
                                    código que se le aplicó.
                                </p>
                                {this.state.paymentDiscountOptions.length === 0 ? (
                                    <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
                                        No hay códigos de tipo "Por medio de pago" cargados. Creá uno en
                                        Códigos de descuento y volvé acá para asignarlo.
                                    </div>
                                ) : null}
                            </div>
                            {/* Sólo los medios elegibles: un descuento por medio de pago sobre
                                «Cuenta de empleado» no tiene sentido —no hay plata que descontar—
                                y se aplicaría en silencio sobre el consumo del equipo (TC-017). */}
                            {selectablePaymentMethods().map(([value, label]) => {
                                const method = Number(value);
                                const selected = this.state.paymentDiscountCodes[method] || '';
                                const option = this.state.paymentDiscountOptions.find((item) => item.code === selected);
                                return (
                                    <div className="col-lg-4" key={value}>
                                        <div className="form-group">
                                            <label>{label}</label>
                                            <select
                                                className="custom-select"
                                                value={selected}
                                                disabled={this.state.paymentDiscountOptions.length === 0}
                                                onChange={(event) => this.setPaymentDiscountCode(method, event.target.value)}
                                            >
                                                <option value="">Sin descuento</option>
                                                {this.state.paymentDiscountOptions.map((item) => (
                                                    <option key={item.code} value={item.code}>
                                                        {item.code} ({item.discount}%)
                                                    </option>
                                                ))}
                                            </select>
                                            {option && option.active === false ? (
                                                <small className="form-text text-danger">
                                                    Este código está fuera de su período de vigencia: hoy no se aplica.
                                                </small>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="col-12">
                                <hr />
                                <h6 className="mb-1">Gift cards</h6>
                                <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                                    Cuántos meses vale una gift card desde el día en que se vende. Vacío = no vencen.
                                    El cambio vale para las que se vendan de acá en adelante: las ya vendidas conservan
                                    el vencimiento con el que salieron.
                                </p>
                            </div>
                            <div className="col-lg-6">
                                <div className="form-group">
                                    <label htmlFor="gift-card-validity-months">Vencimiento de gift cards (meses)</label>
                                    <input
                                        type="number"
                                        id="gift-card-validity-months"
                                        min="1"
                                        step="1"
                                        className="form-control"
                                        placeholder="Vacío = no vencen"
                                        value={this.state.giftCardValidityMonths}
                                        onChange={(event) => this.setState({ giftCardValidityMonths: event.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="col-12">
                                <button className="btn btn-primary" onClick={this.onSubmit} disabled={this.state.isSubmitting}>
                                    Guardar configuración
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default GeneralSettingsComponent;
